/**
 * Requisitos estruturados do pedido (módulo puro, sem rede e sem custo).
 *
 * Transforma o texto livre do utilizador num contrato verificável e, no fim,
 * compara o roteiro gerado com esse contrato. O texto de resumo NUNCA conta
 * como cumprimento: só contam paragens/experiências reais no itinerário.
 */

import { classifyGeoScope, type GeoScopeResult } from "./geo-scope";
import {
  matchTemplateByName,
  type TemplateCategory,
} from "./catalog/curated-route-templates";
import { extractLocalSpecialties } from "./local-specialties";
import { detectMotorcycleMode } from "./transport-modes";
import { parseMultiDestinationPlan } from "./pro/multi-destination";

export type ExperienceKind =
  | "lake"
  | "scenic_train"
  | "nature"
  | "activity"
  | "mountain"
  | "coast"
  | "food"
  | "cultural";

export type TripRequirements = {
  /** Nº de dias explicitamente pedido no texto (undefined = não indicado). */
  requestedDays?: number;
  /** Experiências que TÊM de aparecer no itinerário. */
  requiredExperiences: Array<{ kind: ExperienceKind; minCount: number; label: string }>;
  /** Temas dominantes pedidos, por ordem de importância. */
  preferredThemes: string[];
  /** Experiências de transporte pedidas (ex.: comboio panorâmico). */
  requestedTransportExperiences: Array<"scenic_train" | "boat" | "cable_car" | "road_trip">;
  /** O texto menciona comboio como meio de transporte (sem ser experiência). */
  prefersTrainTransport?: boolean;
  /** O texto pede explicitamente uma viagem de mota. */
  motorcycleTrip?: boolean;
  /** Especialidades gastronómicas nomeadas no pedido. */
  requestedSpecialties?: string[];
  /** O utilizador pediu ajuda com alojamento? */
  accommodationRequest?: { requested: true; priceSensitivity: "budget" | "any" };
  /** Âmbito geográfico do pedido. */
  scope: GeoScopeResult;
};

const WORD_NUMBERS: Record<string, number> = {
  um: 1,
  uma: 1,
  dois: 2,
  duas: 2,
  tres: 3,
  três: 3,
  quatro: 4,
  cinco: 5,
  seis: 6,
  sete: 7,
  oito: 8,
  nove: 9,
  dez: 10,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  uno: 1,
  dos: 2,
  tres_es: 3,
  cuatro: 4,
  cinco_es: 5,
  deux: 2,
  trois: 3,
  quatre: 4,
  cinq: 5,
  due: 2,
  tre: 3,
  quattro: 4,
  cinque: 5,
  zwei: 2,
  drei: 3,
  vier: 4,
  fünf: 5,
};

const DAY_RE =
  /\b(\d{1,2}|um|uma|dois|duas|tr[eê]s|quatro|cinco|seis|sete|oito|nove|dez|one|two|three|four|five|six|seven|eight|nine|ten|uno|dos|cuatro|deux|trois|quatre|cinq|due|tre|quattro|cinque|zwei|drei|vier|f[uü]nf)\s*(?:dias?|days?|d[ií]as?|jours?|giorni?|tage?)\b/iu;

export function extractRequestedDays(prompt: string): number | undefined {
  const match = DAY_RE.exec(prompt ?? "");
  if (!match) return undefined;
  const raw = match[1].toLowerCase();
  const value = /^\d+$/.test(raw) ? Number(raw) : WORD_NUMBERS[raw];
  if (!value || value < 1 || value > 60) return undefined;
  return value;
}

const LAKE_RE = /\b(?:lagos?|lagoas?|lakes?|lac|lacs|seen?|laghi|lago)\b/iu;
const TRAIN_RE =
  /\b(?:comboios?|trem|trens|trains?|zug|z[uü]ge|treni?|trenes?|ferrovi|railway|rail)\b/iu;
/**
 * Um comboio panorâmico só é uma EXPERIÊNCIA quando o texto o qualifica como
 * tal (panorâmico, cénico, conhecido, icónico…) ou nomeia uma rota curada.
 * Uma menção simples a "comboio" é apenas uma preferência de transporte.
 */
const SCENIC_QUALIFIER_RE =
  /\b(?:panor[aâ]mic\w*|cenic\w*|scenic|conhecid\w*|famos\w*|famous|iconic\w*|ic[oó]nic\w*|hist[oó]ric\w*|mitic\w*|lend[aá]ri\w*|tur[ií]stic\w*|paisag\w*|aussichts\w*|panoramic\w*|glacier|express)\b/iu;

const NATURE_RE =
  /\b(?:natureza|nature|natur|naturaleza|paisagens?|landscape|montanhas?|mountains?|trilhos?|hiking|caminhadas?|florestas?|cascatas?)\b/iu;
const ACTIVITY_RE =
  /\b(?:atividades?|actividades?|activities|aktivit[aä]t|attivit[àa]|experi[eê]ncias?|experiences?)\b/iu;
const COAST_RE = /\b(?:praias?|costa|coast|beaches?|litoral|k[üu]ste)\b/iu;
const FOOD_RE = /\b(?:gastronomia|comida|food|cuisine|restaurantes?|gastronom)\b/iu;
const CULTURE_RE = /\b(?:museus?|museums?|cultura|cultural|monumentos?|hist[oó]ri)\b/iu;
const BUDGET_RE =
  /\b(?:barat[oa]s?|econ[oó]mic[oa]s?|low\s*cost|budget|cheap|barato|pre[cç]o\s+baixo|poupar)\b/iu;
const STAY_RE =
  /\b(?:alojamento|hot[eé]l?is?|hotel|hostel|dormir|ficar|accommodation|stay|unterkunft|alloggio|alojamiento)\b/iu;
const BOAT_RE = /\b(?:barco|cruzeiro|ferry|boat|cruise|schiff)\b/iu;
const CABLE_RE = /\b(?:telef[eé]rico|cable\s*car|funicular|gondola|seilbahn)\b/iu;
const ROADTRIP_RE = /\b(?:road\s*trip|de\s+carro\s+pelo|autocaravana|roteiro\s+de\s+carro)\b/iu;

export function extractTripRequirements(
  prompt: string,
  options?: {
    language?: string;
    scopeText?: string;
    curatedExperienceChosen?: boolean;
    curatedExperienceCategory?: TemplateCategory;
  },
): TripRequirements {
  const text = prompt ?? "";
  const scope = classifyGeoScope(options?.scopeText?.trim() || text, {
    language: options?.language ?? "pt",
  });

  const requiredExperiences: TripRequirements["requiredExperiences"] = [];
  const preferredThemes: string[] = [];

  if (LAKE_RE.test(text)) {
    // "alguns dos lagos" ⇒ plural ⇒ pelo menos dois lagos reais.
    const plural = /\b(?:lagos|lakes|seen|laghi|lacs|alguns|v[aá]rios|some|several)\b/iu.test(text);
    requiredExperiences.push({ kind: "lake", minCount: plural ? 2 : 1, label: "lagos" });
    preferredThemes.push("lakes");
  }
  if (NATURE_RE.test(text)) {
    requiredExperiences.push({ kind: "nature", minCount: 2, label: "natureza" });
    preferredThemes.push("nature");
  }
  if (ACTIVITY_RE.test(text)) {
    requiredExperiences.push({ kind: "activity", minCount: 1, label: "atividades" });
    preferredThemes.push("activities");
  }
  if (COAST_RE.test(text)) preferredThemes.push("coast");
  if (FOOD_RE.test(text)) preferredThemes.push("food");
  if (CULTURE_RE.test(text)) preferredThemes.push("cultural");

  const requestedTransportExperiences: TripRequirements["requestedTransportExperiences"] = [];
  const namedTemplate = matchTemplateByName(text);
  const mentionsTrain = TRAIN_RE.test(text);
  const selectedCategory =
    options?.curatedExperienceCategory ??
    (options?.curatedExperienceChosen
      ? // Sem template nomeado, a escolha no seletor vale como experiência
        // panorâmica (categoria por omissão do seletor de experiências).
        (namedTemplate?.category ?? "scenic_train")
      : undefined);

  // Regra genérica: "comboio" sozinho = transporte; só é experiência quando
  // qualificado, nomeado no catálogo, ou escolhido explicitamente pelo
  // utilizador no seletor de experiências.
  const scenicTrainRequested =
    namedTemplate?.category === "scenic_train" ||
    selectedCategory === "scenic_train" ||
    (mentionsTrain && SCENIC_QUALIFIER_RE.test(text));
  if (scenicTrainRequested) {
    requestedTransportExperiences.push("scenic_train");
    requiredExperiences.push({ kind: "scenic_train", minCount: 1, label: "comboio panorâmico" });
    // Uma viagem panorâmica concreta é também uma atividade no horário. Isto
    // não permite que um museu/praça conte como atividade.
    if (!requiredExperiences.some((experience) => experience.kind === "activity")) {
      requiredExperiences.push({ kind: "activity", minCount: 1, label: "atividade" });
    }
    preferredThemes.push("scenic_rail");
  }

  if (BOAT_RE.test(text)) requestedTransportExperiences.push("boat");
  if (CABLE_RE.test(text)) requestedTransportExperiences.push("cable_car");
  if (
    namedTemplate?.category === "road_trip" ||
    namedTemplate?.category === "scenic_road" ||
    selectedCategory === "road_trip" ||
    selectedCategory === "scenic_road" ||
    ROADTRIP_RE.test(text)
  ) {
    requestedTransportExperiences.push("road_trip");
  }

  const wantsBudget = BUDGET_RE.test(text);
  const accommodationRequest =
    STAY_RE.test(text) || (wantsBudget && /\bzonas?\b/iu.test(text))
      ? ({ requested: true, priceSensitivity: wantsBudget ? "budget" : "any" } as const)
      : undefined;

  return {
    // Distribuição por destinos ("2 dias em X, 5 em Y"): os dias pedidos são
    // SEMPRE a soma das paragens, nunca o primeiro número do texto.
    requestedDays: parseMultiDestinationPlan(text)?.totalDays ?? extractRequestedDays(text),
    requiredExperiences,
    preferredThemes: Array.from(new Set(preferredThemes)),
    requestedTransportExperiences,
    prefersTrainTransport: mentionsTrain,
    motorcycleTrip: detectMotorcycleMode(text),
    requestedSpecialties: extractLocalSpecialties(text).map((s) => s.query),
    accommodationRequest,
    scope,
  };
}

/** Transporte global mínimo coerente com o âmbito e com o pedido. */
export function requiredGlobalTransport(
  requirements: TripRequirements,
  current: "walk" | "bike" | "car" | "transit",
): "walk" | "bike" | "car" | "transit" {
  const regional = requirements.scope.kind !== "city";
  if (requirements.requestedTransportExperiences.includes("scenic_train")) return "transit";
  if (requirements.motorcycleTrip) return "car";
  if (requirements.prefersTrainTransport) return "transit";
  if (regional && (current === "walk" || current === "bike")) return "transit";
  return current;
}

// ---------------------------------------------------------------------------
// Verificação de cumprimento
// ---------------------------------------------------------------------------

export type PlanForCheck = {
  days: Array<{
    day: number;
    city?: string;
    stops: Array<{ name: string; category?: string | null; kind?: string | null }>;
  }>;
  transport?: string;
};

export type ComplianceViolation = {
  code:
    | "day_count_mismatch"
    | "missing_experience"
    | "walking_only_regional"
    | "theme_mismatch"
    | "missing_accommodation_zones"
    | "template_ignored"
    | "chronology_overlap"
    | "invalid_time"
    | "late_meal"
    | "day_overflow"
    | "city_mismatch"
    | "transfer_after_activity"
    | "impossible_travel";
  message: string;
  kind?: ExperienceKind;
  missingCount?: number;
};

const EXPERIENCE_MATCHERS: Record<ExperienceKind, RegExp> = {
  lake: /\b(?:lago|lagoa|lake|see|lac|laghi|lakeside|seepromenade)\b|see$/iu,
  scenic_train:
    /\b(?:express|bahn|railway|comboio|train|treno|tren|funicular|jungfrau|bernina|glacier|goldenpass)\b/iu,
  nature:
    /\b(?:parque|park|natur|nature|trail|trilho|jardim|garden|falls|cascata|gorge|glacier|mountain|monte|berg|reserve)\b/iu,
  activity:
    /\b(?:tour|cruise|cruzeiro|boat|barco|telef[eé]rico|cable|kayak|bike|hike|caminhada|passeio|experience|atividade)\b/iu,
  mountain: /\b(?:monte|mount|berg|peak|pico|horn|spitze|montanha)\b/iu,
  coast: /\b(?:praia|beach|coast|costa|strand)\b/iu,
  food: /\b(?:restaurant|restaurante|trattoria|osteria|tasca|bistro|caf[eé]|mercado|market)\b/iu,
  cultural: /\b(?:museu|museum|igreja|church|castelo|castle|palace|pal[aá]cio|gallery|galeria)\b/iu,
};

const CULTURAL_RE = EXPERIENCE_MATCHERS.cultural;
const NATURE_LIKE_RE =
  /(?:parque|park|natur|nature|trail|trilho|lago|lake|see|mountain|monte|berg|falls|cascata|garden|jardim|gorge|glacier|viewpoint|miradouro|cruise|cruzeiro|cable|telef[eé]rico)/iu;

function stopMatches(
  stop: { name: string; category?: string | null; kind?: string | null },
  re: RegExp,
): boolean {
  return re.test(`${stop.name} ${stop.category ?? ""} ${stop.kind ?? ""}`);
}

const LAKE_EXPERIENCE_RE =
  /\b(?:margem|promenade|cais|pier|barco|boat|cruzeiro|cruise|praia|beach|balnear|trilho|trail|aqu[aá]tic|kayak|lakeside|seepromenade|lago|lake|lac|see)\b/iu;
const LAKE_VIEW_ONLY_RE = /\b(?:vista|view|panorama|terrace|terraço)\b/iu;
const ACTIVITY_CATEGORY_RE = /^(?:activity|scenic_train|boat|trail|cable_car|water_activity)$/iu;

export function stopMatchesExperience(
  stop: { name: string; category?: string | null; kind?: string | null },
  kind: ExperienceKind,
): boolean {
  const category = stop.category ?? "";
  const text = `${stop.name} ${category} ${stop.kind ?? ""}`;
  if (kind === "lake") {
    if (category === "lake") return true;
    if (LAKE_VIEW_ONLY_RE.test(stop.name) && !/\b(?:lago|lake|lac|see)\b/iu.test(stop.name))
      return false;
    return EXPERIENCE_MATCHERS.lake.test(text) && LAKE_EXPERIENCE_RE.test(text);
  }
  if (kind === "activity") {
    return (
      stop.kind === "experience" ||
      ACTIVITY_CATEGORY_RE.test(category) ||
      EXPERIENCE_MATCHERS.activity.test(text) ||
      /\b(?:jetboat|rafting|canyoning|paragliding|zipline|safari)\b/iu.test(text)
    );
  }
  return stopMatches(stop, EXPERIENCE_MATCHERS[kind]);
}

export function checkPlanCompliance(
  requirements: TripRequirements,
  plan: PlanForCheck,
  extras?: {
    accommodationZoneCount?: number;
    recognizedTemplateId?: string;
    usedTemplateId?: string;
  },
): ComplianceViolation[] {
  const violations: ComplianceViolation[] = [];
  const allStops = plan.days.flatMap((d) => d.stops ?? []);

  if (requirements.requestedDays && plan.days.length !== requirements.requestedDays) {
    violations.push({
      code: "day_count_mismatch",
      message: `Pediste ${requirements.requestedDays} dias e o plano tem ${plan.days.length}.`,
    });
  }

  for (const experience of requirements.requiredExperiences) {
    const count = allStops.filter((s) => stopMatchesExperience(s, experience.kind)).length;
    if (count < experience.minCount) {
      violations.push({
        code: "missing_experience",
        message: `O pedido exige ${experience.minCount} × ${experience.label} no itinerário e só encontrei ${count}.`,
        kind: experience.kind,
        missingCount: experience.minCount - count,
      });
    }
  }

  const regional = requirements.scope.kind !== "city";
  if (
    regional &&
    (plan.transport === "walk" || plan.transport === "bike") &&
    plan.days.length > 1
  ) {
    violations.push({
      code: "walking_only_regional",
      message:
        "Um roteiro regional não pode ter transporte global “A pé”. Separa o interurbano do transporte local.",
    });
  }

  const wantsNature = requirements.preferredThemes.some((t) =>
    ["nature", "lakes", "activities"].includes(t),
  );
  if (wantsNature && allStops.length >= 4) {
    const cultural = allStops.filter((s) => stopMatches(s, CULTURAL_RE)).length;
    const natureLike = allStops.filter((s) => stopMatches(s, NATURE_LIKE_RE)).length;
    if (cultural > natureLike) {
      violations.push({
        code: "theme_mismatch",
        message: "Pediste natureza, mas a maioria das paragens são museus/monumentos.",
      });
    }
  }

  if (requirements.accommodationRequest && (extras?.accommodationZoneCount ?? 0) < 2) {
    violations.push({
      code: "missing_accommodation_zones",
      message:
        "Pediste zonas mais baratas: preciso de comparar pelo menos 2 zonas, não apenas um hotel.",
    });
  }

  if (extras?.recognizedTemplateId && extras.usedTemplateId !== extras.recognizedTemplateId) {
    violations.push({
      code: "template_ignored",
      message: "Reconheci uma rota famosa no pedido mas ela não foi usada no plano.",
    });
  }

  return violations;
}

/** Um plano só é entregável quando não há violações bloqueantes. */
export function isPlanAcceptable(violations: ComplianceViolation[]): boolean {
  return violations.length === 0;
}

// ---------------------------------------------------------------------------
// Validação do handoff Premium → Pro ANTES de reservar créditos
// ---------------------------------------------------------------------------

export type HandoffContractCheck = {
  /** Dias definidos no formulário estruturado (Pro). */
  requestedDays?: number;
  /** Cidades já derivadas (ex.: pelo planeador regional). */
  cities?: string[];
  /** Transporte global escolhido no formulário. */
  transport?: string;
};

export type HandoffConflict = {
  code: "days_mismatch" | "single_city_for_region" | "transport_conflict";
  message: string;
};

/**
 * Compara o formulário estruturado com o texto livre original. Isto corre
 * SEMPRE antes de reservar uso do Pro — nunca depois — para que um conflito
 * não consuma créditos.
 */
export function validateHandoffAgainstRequirements(
  requirements: TripRequirements,
  handoff: HandoffContractCheck,
): { valid: boolean; conflicts: HandoffConflict[] } {
  const conflicts: HandoffConflict[] = [];

  if (
    requirements.requestedDays != null &&
    handoff.requestedDays != null &&
    requirements.requestedDays !== handoff.requestedDays
  ) {
    conflicts.push({
      code: "days_mismatch",
      message: `Pediste ${requirements.requestedDays} dias no texto, mas o formulário tem ${handoff.requestedDays}.`,
    });
  }

  if (requirements.scope.kind !== "city" && (handoff.cities?.length ?? 0) <= 1) {
    conflicts.push({
      code: "single_city_for_region",
      message: `${requirements.scope.name} é um destino regional/nacional; não pode ser tratado como uma única cidade.`,
    });
  }

  if (
    requirements.requestedTransportExperiences.includes("scenic_train") &&
    handoff.transport &&
    handoff.transport !== "transit" &&
    handoff.transport !== "train"
  ) {
    conflicts.push({
      code: "transport_conflict",
      message:
        "Pediste um comboio panorâmico, mas o transporte escolhido não é transportes públicos.",
    });
  }

  return { valid: conflicts.length === 0, conflicts };
}
